/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentType } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { cascadeEval } from 'utils/conditions';

import { RoutePermissionConfig, routes, shouldBlockRoute } from 'routes/config';
import { useAuth } from 'providers/Auth';

import { PageTitle } from './Title';

interface BaseProps extends RoutePermissionConfig {
  isPrivate?: boolean;
  component?: ComponentType;
  redirect?: string;
  permanent?: boolean;
  pageKey?: string;

  title?: string;
}

type RouteOptions =
  | { component: ComponentType; redirect?: never }
  | { component?: never; redirect: string };

export type CustomRouteProps = BaseProps & RouteOptions;

const REDIRECT_PATH_PARAM = '__redirect';

const HOME = routes.home.path;

export function CustomRoute({
  isPrivate = true,
  component: Component,
  redirect,
  pageKey,
  permanent,
  title,
  customPermissionCheck,
  permissions,
}: CustomRouteProps) {
  const { pathname } = useLocation();
  const [search] = useSearchParams();

  const { isLogged, data } = useAuth();

  const RenderComponent = Component as any;

  if (redirect) return <Navigate to={redirect} replace />;

  if (!isPrivate && permanent) return <RenderComponent key={pageKey} />;

  const noPermission = cascadeEval([
    { is: !isLogged, then: false },
    {
      is: !!permissions || customPermissionCheck,
      then: () => shouldBlockRoute({ permissions, customPermissionCheck }, data?.user),
    },
    { is: true, then: false },
  ]);

  if (noPermission) return <Navigate to={HOME} replace />;

  const redirectAfter = isPrivate && pathname !== HOME;

  const loggedPath = decodeURIComponent(search.get(REDIRECT_PATH_PARAM) ?? HOME);

  return isPrivate === isLogged ? (
    RenderComponent && (
      <PageTitle title={title}>
        <RenderComponent key={pageKey} />
      </PageTitle>
    )
  ) : (
    <Navigate
      replace
      to={{
        pathname: isLogged ? loggedPath : '/auth',

        search: redirectAfter
          ? `?${REDIRECT_PATH_PARAM}=${encodeURIComponent(pathname)}`
          : undefined,
      }}
    />
  );
}
